import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Users, Plus, X, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RepList = ({ title, emptyLabel, placeholder, items, inputValue, onInputChange, onAdd, onRemove, testPrefix }) => (
  <div className="bg-card border border-border rounded-xl p-6">
    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
      <Users size={20} />
      {title}
    </h3>
    <div className="flex gap-2 mb-4">
      <input
        data-testid={`${testPrefix}-input`}
        type="text"
        value={inputValue}
        onChange={(e) => onInputChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onAdd();
          }
        }}
        placeholder={placeholder}
        className="flex-1 px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
      />
      <button
        data-testid={`${testPrefix}-add-btn`}
        type="button"
        onClick={onAdd}
        className="flex items-center gap-1 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
      >
        <Plus size={16} /> Add
      </button>
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-muted-foreground">No {emptyLabel} names added yet.</p>
    ) : (
      <div className="space-y-2">
        {items.map((name) => (
          <div
            key={name}
            className="flex items-center justify-between px-4 py-2.5 bg-secondary border border-border rounded-lg"
          >
            <span data-testid={`${testPrefix}-item-${name}`} className="text-sm font-medium">
              {name}
            </span>
            <button
              data-testid={`${testPrefix}-remove-${name}`}
              type="button"
              onClick={() => onRemove(name)}
              className="text-muted-foreground hover:text-red-400 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const SalesRepsManager = () => {
  const [repLists, setRepLists] = useState({ isr_list: [], osr_list: [] });
  const [newIsr, setNewIsr] = useState("");
  const [newOsr, setNewOsr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    axios
      .get(`${API}/settings/reps`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setRepLists(res.data))
      .catch(() => {});
  }, []);

  const handleAdd = (type) => {
    const val = type === "isr" ? newIsr.trim() : newOsr.trim();
    if (!val) return;
    const key = type === "isr" ? "isr_list" : "osr_list";
    if (repLists[key].includes(val)) {
      toast.error("Name already exists");
      return;
    }
    setRepLists((prev) => ({ ...prev, [key]: [...prev[key], val] }));
    if (type === "isr") setNewIsr("");
    else setNewOsr("");
  };

  const handleRemove = (type, name) => {
    const key = type === "isr" ? "isr_list" : "osr_list";
    setRepLists((prev) => ({ ...prev, [key]: prev[key].filter((n) => n !== name) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/settings/reps`, repLists, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Sales assignments saved");
    } catch (err) {
      toast.error("Failed to save sales assignments");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <RepList
        title="Sales Ops"
        emptyLabel="Sales Ops"
        placeholder="Add Sales Ops name..."
        items={repLists.isr_list}
        inputValue={newIsr}
        onInputChange={setNewIsr}
        onAdd={() => handleAdd("isr")}
        onRemove={(name) => handleRemove("isr", name)}
        testPrefix="isr"
      />
      <RepList
        title="Outside Sales Reps (OSR)"
        emptyLabel="OSR"
        placeholder="Add OSR name..."
        items={repLists.osr_list}
        inputValue={newOsr}
        onInputChange={setNewOsr}
        onAdd={() => handleAdd("osr")}
        onRemove={(name) => handleRemove("osr", name)}
        testPrefix="osr"
      />
      <button
        data-testid="save-reps-btn"
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
      >
        <Save size={18} />
        {saving ? "Saving..." : "Save Sales Assignments"}
      </button>
    </div>
  );
};
